import React, { useState, useEffect } from 'react';
import { X, Clock } from 'lucide-react';
import { ServiceAddon, getServiceAddons } from '../../lib/services';

interface SelectServiceAddonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  onSelect: (addons: ServiceAddon[]) => void;
  selectedAddons?: ServiceAddon[];
}

export default function SelectServiceAddonsModal({
  isOpen,
  onClose,
  serviceId,
  onSelect,
  selectedAddons = []
}: SelectServiceAddonsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set(selectedAddons.map(a => a.id)));

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadAddons();
    }
  }, [isOpen, serviceId]);

  const loadAddons = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getServiceAddons(serviceId);
      setAddons(data);
    } catch (err) {
      console.error('Error loading add-ons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSelect = (addon: ServiceAddon) => {
    const newSelected = new Set(selectedAddonIds);
    if (newSelected.has(addon.id)) {
      newSelected.delete(addon.id);
    } else {
      newSelected.add(addon.id);
    }
    setSelectedAddonIds(newSelected);
  };

  const handleSave = () => {
    const selectedAddons = addons.filter(addon => selectedAddonIds.has(addon.id));
    onSelect(selectedAddons);
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100000]`}
      onClick={onClose}
      style={{
        zIndex: 100000,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Select Add-ons</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : addons.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No add-ons available for this service
              </div>
            ) : (
              <div className="space-y-4">
                {addons.map(addon => (
                  <div
                    key={addon.id}
                    className={`border rounded-lg transition-colors cursor-pointer ${
                      selectedAddonIds.has(addon.id)
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleSelect(addon)}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">
                          {addon.name}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          ${addon.price.toLocaleString()}
                        </div>
                      </div>
                      {addon.description && (
                        <div className="text-sm text-gray-500 mb-2">
                          {addon.description}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1.5" />
                        {addon.duration_minutes >= 1440 
                          ? `${Math.round(addon.duration_minutes / 1440)} days`
                          : `${Math.round(addon.duration_minutes / 60)} hours`
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Selected Add-ons
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 